import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "../components/Head";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { sectionToSlug, slugToSection } from "../constants/filterSlugs";
import SelectField from "../components/SelectField";
import loadEcharts from "../functions/echartsLoader";
import {
  CAGR_ORDER,
  METRIC_LABELS_CZ,
  RATIO_GROUPS,
  buildBarOption,
  buildLineOption,
  fmt,
  fmtInt,
  fmtPercent,
  safeNum,
  styles
} from "../functions/sectionReportCalculations";

import { fetchFullReport, fetchStats } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { SECTION_NAMES } from "../content/site";
import "./style/analytics.css";

const SUMMARY_PERIOD_LABEL = { period_all: "2016–2025", period_2021_2024: "2021–2025", period_2023_2024: "2023–2025" };
const fmtDays = (v) => (Number.isFinite(v) ? `${v.toFixed(0)} dní` : "—");


const SectionReportPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { obor } = useParams();

  // --- Selection State ---
  const [allSections, setAllSections] = useState([]);
  const [allL1, setAllL1] = useState([]);
  const [allL2, setAllL2] = useState([]);

  const [lvl1Options, setLvl1Options] = useState([]);
  const [lvl2Options, setLvl2Options] = useState([]);

  const [selectedSekce, setSelectedSekce] = useState(() => slugToSection(obor) || "F");
  const [selectedL1, setSelectedL1] = useState("");
  const [selectedL2, setSelectedL2] = useState("");

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters within charts
  const [cagrWindow, setCagrWindow] = useState(null);
  const [indexWindow, setIndexWindow] = useState("2016_2025");
  
  // Table Filter
  const [summaryPeriod, setSummaryPeriod] = useState("period_all");
  const [isTableOpen, setIsTableOpen] = useState(false);
  
  // Refs
  const chartsReady = useRef(false);
  const cagrChartRef = useRef(null);
  const indexChartRef = useRef(null);
  const rentabilitaRef = useRef(null);
  const likviditaRef = useRef(null);
  const zadluzenostRef = useRef(null);
  const obratkovostRef = useRef(null);
  const isPublicReport = location.pathname.startsWith("/oborove-statistiky");
  const isRestrictedView = isPublicReport && !user;

  const openAuthPrompt = (mode = "register") => {
    const params = new URLSearchParams(location.search);
    params.set("auth", mode);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  const sectionSelectOptions = useMemo(
    () => allSections.map((s) => ({
      value: s.Sekce,
      label: `${s.Sekce} - ${s["Sekce název"]}`,
    })),
    [allSections]
  );

  const level1SelectOptions = useMemo(
    () => [
      { value: "", label: "-- Vyberte skupinu --" },
      ...lvl1Options.map((l) => ({
        value: l["1 úroveň"],
        label: `${l["1 úroveň"]} - ${l["1 úroveň název"]}${isRestrictedView ? " 🔒" : ""}`,
      })),
    ],
    [lvl1Options, isRestrictedView]
  );

  const level2SelectOptions = useMemo(
    () => [
      { value: "", label: "-- Vyberte podskupinu --" },
      ...lvl2Options.map((l) => ({
        value: l["2 úroveň ID"],
        label: `${l["2 úroveň ID"]} - ${l["2 úroveň název"]}${isRestrictedView ? " 🔒" : ""}`,
      })),
    ],
    [lvl2Options, isRestrictedView]
  );

  // --- 1. Init: Load Reference Data ---
  useEffect(() => {
    loadEcharts().then(() => { chartsReady.current = true; });
    const loadStructure = async () => {
        try {
            const [secData, l1Data, l2Data] = await Promise.all([
                fetchStats("sections", "all"),
                fetchStats("level1", "all"),
                fetchStats("level2", "all")
            ]);
            setAllSections(secData);
            setAllL1(l1Data);
            setAllL2(l2Data);
        } catch (e) {
            console.error("Failed to load structure", e);
        }
    };
    loadStructure();
  }, []);

  const handleRestrictedSelect = (nextValue, setter, allowedValues = []) => {
    if (!isRestrictedView || allowedValues.includes(nextValue)) {
      setter(nextValue);
      return;
    }
    openAuthPrompt("register");
  };

  useEffect(() => {
    if (!isRestrictedView) return;
    if (selectedL1 !== "") setSelectedL1("");
    if (selectedL2 !== "") setSelectedL2("");
  }, [isRestrictedView, selectedL1, selectedL2]);


useEffect(() => {
    const sectionFromPath = slugToSection(obor) || "F";
    setSelectedSekce((prev) => (prev !== sectionFromPath ? sectionFromPath : prev));
  }, [obor]);

  useEffect(() => {
    const nextSlug = sectionToSlug(selectedSekce);
    const nextPathname = nextSlug ? `/oborove-statistiky/${nextSlug}` : "/oborove-statistiky";
    const params = new URLSearchParams(location.search);
    const authParam = params.get("auth");
    const nextSearch = authParam ? `?auth=${authParam}` : "";

    if (`${location.pathname}${location.search}` !== `${nextPathname}${nextSearch}`) {
      navigate(`${nextPathname}${nextSearch}`, { replace: true });
    }
  }, [selectedSekce, location.pathname, location.search, navigate]);

  // --- 2. Cascade Logic ---
  useEffect(() => {
    if (!selectedSekce) { setLvl1Options([]); return; }
    const filteredL1 = allL1.filter(row => row.Sekce === selectedSekce);
    filteredL1.sort((a,b) => a["1 úroveň"] - b["1 úroveň"]);
    setLvl1Options(filteredL1);
    setSelectedL1("");
    setSelectedL2("");
  }, [selectedSekce, allL1]);

  useEffect(() => {
    if (!selectedL1) { setLvl2Options([]); return; }
    const filteredL2 = allL2.filter(row =>
        row.Sekce === selectedSekce &&
        String(row["1 úroveň"]) === String(selectedL1)
    );
    filteredL2.sort((a,b) => String(a["2 úroveň ID"]).localeCompare(String(b["2 úroveň ID"])));
    setLvl2Options(filteredL2);
    setSelectedL2("");
  }, [selectedL1, allL2, selectedSekce]);


  // --- 3. Fetch Report Logic ---
  useEffect(() => {
    if (!selectedSekce) return;

    let levelType = "sekce";
    let itemId = selectedSekce;

    if (selectedL2) {
        levelType = "l2";
        itemId = selectedL2;
    } else if (selectedL1) {
        levelType = "l1";
        itemId = selectedL1;
    }

    setLoading(true);
    setError(null);

    fetchFullReport(levelType, itemId)
      .then(data => {
        setReportData(data);
        if (data.growth_cagr && data.growth_cagr.length) {
            const maxYears = Math.max(...data.growth_cagr.map(r => r.window_years));
            const bestRow = data.growth_cagr.find(r => r.window_years === maxYears);
            if (bestRow) setCagrWindow(`${bestRow.window_start}-${bestRow.window_end}`);
        }
      })
      .catch(err => {
          console.error(err);
          setError(err.message);
          setReportData(null);
      })
      .finally(() => setLoading(false));

  }, [selectedSekce, selectedL1, selectedL2]);


  // --- 4. Data Processing ---

  const cagrDataProcessed = useMemo(() => {
    if (!reportData?.growth_cagr || !cagrWindow) return [];
    const [start, end] = cagrWindow.split("-").map(Number);
    const rows = reportData.growth_cagr.filter(r => r.window_start === start && r.window_end === end);
    return CAGR_ORDER.map(key => {
        const row = rows.find(r => r.metric.toLowerCase().includes(key));
        return { label: METRIC_LABELS_CZ[key], value: row ? row.cagr_median : 0 };
    });
  }, [reportData, cagrWindow]);

  // List of available CAGR windows
  const cagrWindowsList = useMemo(() => {
      if (!reportData?.growth_cagr) return [];
      const wins = new Set(reportData.growth_cagr.map(r => `${r.window_start}-${r.window_end}`));
      return Array.from(wins).sort().reverse();
  }, [reportData]);

  // --- MODIFIED INDEX CHART LOGIC (FIXED) ---
  const indexChartData = useMemo(() => {
    // 1. Проверка наличия данных
    if (!reportData?.indices || !reportData?.financial_ratios?.history) return null;

    // 2. Фильтрация индексов по окну
    const filteredIndices = reportData.indices.filter(r => r.window === indexWindow);
    if (!filteredIndices.length) return null;

    // 3. Поиск строк с "Tržby"
    const revenueRows = filteredIndices.filter(r => 
        r.metric && r.metric.toLowerCase().includes("tržby")
    );

    if (!revenueRows.length) return null;

    // 4. Получение списка лет
    const years = [...new Set(revenueRows.map(r => r.Year))].sort((a,b) => a - b);

    // 5. История коэффициентов (для маржи)
    const ratioHistory = reportData.financial_ratios.history;

    // 6. Формирование серий
    const revenueSeriesData = years.map(y => {
        // Используем String() для безопасного сравнения года
        const row = revenueRows.find(r => String(r.Year) === String(y));
        return row ? safeNum(row.index_median) : null;
    });

    const ebitdaSeriesData = years.map(y => {
        // A. Находим индекс выручки
        const revRow = revenueRows.find(r => String(r.Year) === String(y));
        
        // B. Находим маржу (Важно: String(r.Year) === String(y))
        const ratioRow = ratioHistory.find(r => String(r.Year) === String(y));

        if (!revRow || !ratioRow) return null;

        const revenueIndexVal = safeNum(revRow.index_median);
        const marginVal = safeNum(ratioRow["EBITDA marže__median"]);
        
        // Если маржа пришла null или undefined, результат будет 0
        return revenueIndexVal * marginVal;
    });

    return { 
        years, 
        series: [
            {
                name: "Tržby celkem",
                data: revenueSeriesData
            },
            {
                name: "EBITDA (odvozeno)",
                data: ebitdaSeriesData
            }
        ]
    };
  }, [reportData, indexWindow]);

  const indexWindowsList = useMemo(() => {
      if (!reportData?.indices) return ["2016_2025"];
      return Array.from(new Set(reportData.indices.map(r => r.window))).sort().reverse();
  }, [reportData]);

  const ratiosDataHelper = (groupKey) => {
    if (!reportData?.financial_ratios?.history) return null;
    const rows = [...reportData.financial_ratios.history].sort((a, b) => a.Year - b.Year);
    const years = rows.map(r => r.Year);
    const series = RATIO_GROUPS[groupKey].map(def => ({
        name: def.name,
        data: rows.map(r => safeNum(r[def.key]))
    }));
    return { years, series };
  };

  const rentabilitaData = useMemo(() => ratiosDataHelper('rentabilita'), [reportData]);
  const likviditaData = useMemo(() => ratiosDataHelper('likvidita'), [reportData]);
  const zadluzenostData = useMemo(() => ratiosDataHelper('zadluzenost'), [reportData]);
  const obratkovostData = useMemo(() => ratiosDataHelper('obratkovost'), [reportData]);

  // --- 5. Render Charts ---
  useEffect(() => {
    if (!chartsReady.current || !window.echarts || !reportData || loading) return;
    
    const renderChart = (ref, option) => {
        if (ref.current && option) {
            const chart = window.echarts.getInstanceByDom(ref.current) || window.echarts.init(ref.current);
            chart.setOption(option);
            return chart;
        }
    };

    renderChart(cagrChartRef, buildBarOption(cagrDataProcessed));

    if (indexChartData) renderChart(indexChartRef, buildLineOption(indexChartData.years, indexChartData.series));

    if (rentabilitaData) renderChart(rentabilitaRef, buildLineOption(rentabilitaData.years, rentabilitaData.series, (v) => (v*100).toFixed(1)+"%"));
    if (likviditaData) renderChart(likviditaRef, buildLineOption(likviditaData.years, likviditaData.series, (v) => v.toFixed(2)));
    if (zadluzenostData) renderChart(zadluzenostRef, buildLineOption(zadluzenostData.years, zadluzenostData.series, (v) => (v*100).toFixed(1)+"%"));

    if (obratkovostData) renderChart(obratkovostRef, buildLineOption(obratkovostData.years, obratkovostData.series, (v) => v.toFixed(2)));

    const handleResize = () => {
        [cagrChartRef, indexChartRef, rentabilitaRef, likviditaRef, zadluzenostRef, obratkovostRef].forEach(ref => {
            window.echarts.getInstanceByDom(ref.current)?.resize();
        });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);

  }, [cagrDataProcessed, indexChartData, rentabilitaData, likviditaData, zadluzenostData, obratkovostData, loading]);

  // --- HELPER LABELS & CONTEXT ---
  const selectedSectionFullLabel = useMemo(() => {
    if (!selectedSekce || selectedSekce === "Vše") return "Všechny sekce";
    const match = allSections.find((s) => s.Sekce === selectedSekce);
    return match ? ` ${match["Sekce název"]}` : selectedSekce;
  }, [selectedSekce, allSections]);

  const selectedL1FullLabel = useMemo(() => {
    if (!selectedL1 || selectedL1 === "Vše") return "Všechny obory (L1)";
    const match = allL1.find(
      l =>
        String(l["1 úroveň"]) === String(selectedL1) &&
        l.Sekce === selectedSekce
    );
    return match
      ? `${match["1 úroveň"]} - ${match["1 úroveň název"]}`
      : selectedL1;
  }, [selectedL1, selectedSekce, allL1]);

  const selectedL2FullLabel = useMemo(() => {
    if (!selectedL2 || selectedL2 === "Vše") return "Všechny dílčí obory (L2)";
    const match = allL2.find(
      l =>
        String(l["2 úroveň ID"]) === String(selectedL2) &&
        String(l["1 úroveň"]) === String(selectedL1) &&
        l.Sekce === selectedSekce
    );
    return match
      ? `${match["2 úroveň ID"]} - ${match["2 úroveň název"]}`
      : selectedL2;
  }, [selectedL2, selectedL1, selectedSekce, allL2]);

  // Determine current context for description
  const currentContext = useMemo(() => {
      if (selectedL2) return "lvl2";
      if (selectedL1) return "lvl1";
      return "sections";
  }, [selectedL1, selectedL2]);

  // --- MEDIAN DESCRIPTION LOGIC ---
  const medianDescription = useMemo(() => {
      const baseNote = "Hodnota ukazuje medián vybraného ukazatele napříč vybranými roky; hodnoty jsou očištěny od extrémů.";
      const periodLabel = "2016–2025";

      if (currentContext === "sections") {
        return `Detailní pohled na sekci ${selectedSectionFullLabel} za období ${periodLabel}. ${baseNote}`;
      }
  
      if (currentContext === "lvl1") {
        return `Detailní pohled na skupinu ${selectedL1FullLabel} za období ${periodLabel}. ${baseNote}`;
      }
  
      if (currentContext === "lvl2") {
        return `Detailní pohled na obor ${selectedL2FullLabel} za období ${periodLabel}. ${baseNote}`;
      }
      return "";
  }, [currentContext, selectedSectionFullLabel, selectedL1FullLabel, selectedL2FullLabel]);


  // --- Helper for Bottom Table (Aggregated) ---
  const aggregatedRow = useMemo(() => {
      if (!reportData?.financial_ratios) return null;
      const arr = reportData.financial_ratios[summaryPeriod];
      return arr && arr.length > 0 ? arr[0] : null;
  }, [reportData, summaryPeriod]);

  const selectedLabel = useMemo(() => {
    if (!reportData?.meta) return "Vybrané odvětví";
    const id = reportData.meta.id || "";
    const name = reportData.meta.name || "";
    if (reportData.meta.level === "sekce" && SECTION_NAMES[id]) return `${SECTION_NAMES[id]} (sekce ${id})`;
    return name ? `${name} (${id})` : id || "Vybrané odvětví";
  }, [reportData]);

  const seoTitle = useMemo(() => {
    return `${selectedLabel} – finanční ukazatele a vývoj | FinPilot`;
  }, [selectedLabel]);

  const seoDescription = useMemo(() => {
    return `Detailní analýza odvětví ${selectedLabel}: časové řady ukazatelů (rentabilita, likvidita, zadluženost, obratkovost), CAGR a indexy aktivity za období 2016–2025.`;
  }, [selectedLabel]);

  const canonicalUrl = useMemo(() => {
    const slug = sectionToSlug(selectedSekce);
    return slug
      ? `https://finpilot.cz/oborove-statistiky/${slug}`
      : "https://finpilot.cz/oborove-statistiky";
  }, [selectedSekce]);

  const sectionReportSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: seoTitle,
    description: seoDescription,
    url: canonicalUrl,
    inLanguage: "cs-CZ",
    isAccessibleForFree: true,
    creator: {
      "@type": "Organization",
      name: "FinPilot",
      url: "https://finpilot.cz",
      logo: "https://finpilot.cz/logo512.png"
    },
    publisher: {
      "@type": "Organization",
      name: "FinPilot",
      url: "https://finpilot.cz",
      logo: "https://finpilot.cz/logo512.png"
    },
    keywords: ["oborové statistiky", "NACE", "CAGR", "rentabilita", "likvidita", "finanční analýza"]
  }), [canonicalUrl, seoDescription, seoTitle]);


  return (

    <div className="stats-echarts-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://finpilot.cz/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://finpilot.cz/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(sectionReportSchema)}</script>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>


      <header className="page-head">
        <div>
          <p className="eyebrow">Modul Reporty</p>
          <h1>{reportData?.meta ? `${selectedLabel}: finanční ukazatele a vývoj` : "Detailní analýza odvětví"}</h1>
          <p className="lead">
            Vyberte sekci a odvětví (NACE) a zobrazte vývoj klíčových finančních ukazatelů,
            růst (CAGR) a indexy aktivity v období 2016-2025.
          </p>
        </div>
      </header>


      {/* --- FILTERS --- */}
      <section className="card" style={{ marginBottom: '2rem' }}>
        <div className="filters" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div className="control-group">
                <label>1. Sekce (NACE)</label>
                <SelectField
                  value={selectedSekce}
                  onChange={(e) => setSelectedSekce(e.target.value)}
                  options={sectionSelectOptions}
                />
            </div>
            <div className="control-group">
                <label>2. Skupina (1. úroveň)</label>
                <SelectField
                  value={selectedL1}
                  onChange={(e) =>
                    handleRestrictedSelect(e.target.value, setSelectedL1, [""])
                  }
                  options={level1SelectOptions}
                  disabled={lvl1Options.length === 0}
                  helperText={
                    isRestrictedView
                      ? "Detailní skupiny jsou dostupné po registraci."
                      : undefined
                  }
                  selectClassName={`big-select${isRestrictedView ? " is-locked" : ""}`}
                />
            </div>
            <div className="control-group">
                <label>3. Podskupina (2. úroveň)</label>
                <SelectField
                  value={selectedL2}
                  onChange={(e) =>
                    handleRestrictedSelect(e.target.value, setSelectedL2, [""])
                  }
                  options={level2SelectOptions}
                  disabled={lvl2Options.length === 0 && !isRestrictedView}
                  onClick={isRestrictedView ? () => openAuthPrompt("register") : undefined}
                  helperText={
                    isRestrictedView
                      ? "Podskupiny jsou dostupné po registraci."
                      : undefined
                  }
                  selectClassName={`big-select${isRestrictedView ? " is-locked" : ""}`}
                />
            </div>
        </div>

        {reportData?.meta && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #027360' }}>
                <small style={{textTransform:'uppercase', color:'#666', fontSize:'0.75rem', fontWeight:'bold'}}>
                    {reportData.meta.level === "sekce" ? "Sekce" : reportData.meta.level === "l1" ? "1. Úroveň" : "2. Úroveň"}
                </small>
                <h2 style={{ margin: '0.2rem 0 0 0', fontSize: '1.4rem', color: '#0b1f2e' }}>
                    {reportData.meta.name || reportData.meta.id}
                    <span style={{ color: '#888', fontWeight: 'normal', marginLeft: '0.5rem' }}>({reportData.meta.id})</span>
                </h2>
            </div>
        )}
        {loading && !reportData?.meta && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #027360' }}>
            <div className="skeleton-line" style={{ width: "120px", marginBottom: "8px" }} />
            <div className="skeleton-line" style={{ width: "60%", height: "18px" }} />
          </div>
        )}
      </section>

      {loading && (
        <>
          <section className="card-grid">
            <div className="card">
              <div className="card__header">
                <h4>Růst (CAGR)</h4>
                <div className="window-switcher small">
                  <span className="skeleton-pill" />
                  <span className="skeleton-pill" />
                  <span className="skeleton-pill" />
                </div>
              </div>
              <p style={styles.description}>CAGR sleduje mediánový růst firem v oboru ve vybraném období.</p>
              <div className="chart-slot" style={{ height: '320px' }}>
                <div className="skeleton-block" />
              </div>
            </div>
            <div className="card">
              <div className="card__header">
                <h4>Indexy Aktivity</h4>
                <div className="window-switcher small">
                  <span className="skeleton-pill" />
                  <span className="skeleton-pill" />
                  <span className="skeleton-pill" />
                </div>
              </div>
              <p style={styles.description}>Index sleduje vývoj absolutních hodnot mediánové firmy v oboru.</p>
              <div className="chart-slot" style={{ height: '320px' }}>
                <div className="skeleton-block" />
              </div>
            </div>
          </section>

          <section className="report-section report-section--health">
            <div className="report-section__head">
              <h2>Finanční zdraví</h2>
              <div className="skeleton-line" style={{ width: "70%", marginTop: "6px" }} />
            </div>

            <section className="card-grid quartet">
              {Array.from({ length: 4 }).map((_, index) => (
                <div className="card" key={`health-skeleton-${index}`}>
                  <div className="card__header">
                    <div className="skeleton-line" style={{ width: "55%" }} />
                  </div>
                  <div className="chart-slot" style={{ height: '300px' }}>
                    <div className="skeleton-block" />
                  </div>
                </div>
              ))}
            </section>
          </section>
        </>
      )}
      {error && <div className="error-message">Chyba: {error}</div>}

      {!loading && reportData && (
        <>
            <section className="card-grid">
                <div className="card">
                    <div className="card__header">
                        <h4>Růst (CAGR)</h4>
                        {/* --- CHANGED TO BUTTONS (WINDOW SWITCHER) --- */}
                        <div className="window-switcher small">
                            {cagrWindowsList.map((w) => (
                                <button
                                    key={w}
                                    className={cagrWindow === w ? "pill active" : "pill"}
                                    onClick={() => setCagrWindow(w)}
                                >
                                    {w}
                                </button>
                            ))}
                        </div>
                    </div>
                    <p style={styles.description}>CAGR sleduje mediánový růst firem v oboru ve vybraném období.</p>
                    <div className="chart" ref={cagrChartRef} style={{ height: '320px' }} />
                </div>
                <div className="card">
                    <div className="card__header">
                        <h4>Indexy Aktivity</h4>
                        <div className="window-switcher small">
                            {indexWindowsList.map(w => (
                                <button key={w} className={indexWindow === w ? "pill active" : "pill"} onClick={() => setIndexWindow(w)}>
                                    {w.replace("_", "-")}
                                </button>
                            ))}
                        </div>
                    </div>
                    <p style={styles.description}>Index sleduje vývoj absolutních hodnot mediánové firmy v oboru.</p>
                    <div className="chart" ref={indexChartRef} style={{ height: '320px' }} />
                </div>
            </section>

            <section className="report-section report-section--health">
              {/* SECTION HEADER: Added medianDescription here */}
              <div className="report-section__head">
                <h2>Finanční zdraví</h2>
                <p style={{ maxWidth: '800px' , color: '#5f6b6f' }}>
                   {medianDescription}
                </p>
              </div>

            <section className="card-grid quartet">
                <div className="card">
                    <div className="card__header"><h4>Rentabilita (Profitability)</h4></div>
                    <div className="chart" ref={rentabilitaRef} style={{ height: '300px' }} />
                </div>
                <div className="card">
                    <div className="card__header"><h4>Likvidita (Liquidity)</h4></div>
                    <div className="chart" ref={likviditaRef} style={{ height: '300px' }} />
                </div>
                <div className="card">
                    <div className="card__header"><h4>Zadluženost (Debt)</h4></div>
                    <div className="chart" ref={zadluzenostRef} style={{ height: '300px' }} />
                </div>
                <div className="card">
                    <div className="card__header"><h4>Obratkovost (Activity)</h4></div>
                    <div className="chart" ref={obratkovostRef} style={{ height: '300px' }} />
                </div>
            </section>


            {/* --- TABLE: HISTORY --- */}
            <section className="card" style={{ marginTop: '2rem' , padding: '0.5rem', paddingTop: '2rem', paddingBottom: '2rem' }}>
                  <div
                    className="card__header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer"
                      , paddingLeft: '1.5rem', paddingRight: '1.5rem'
                    }}
                    onClick={() => setIsTableOpen(v => !v)}
                  >
                    <h4 style={{ margin: 0 }}>
                      📋 Detailní tabulka statistik (Historie)
                    </h4>

                    <span style={{ fontSize: "0.9rem", color: "#027360" }}>
                      {isTableOpen ? "▲ Skrýt" : "▼ Zobrazit"}
                    </span>
                  </div>

                  {isTableOpen && (
                    <div className="table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Rok</th>
                                    <th>EBITDA Marže</th>
                                    <th>Provozní Marže</th>
                                    <th>ROA</th>
                                    <th>ROE</th>
                                    <th>ROS</th>
                                    <th>Likvidita Běžná</th>
                                    <th>Likvidita Pohotová</th>
                                    <th>Likvidita Okamžitá</th>
                                    <th>Zadluženost Celk.</th>
                                    <th>Zadluženost Dlouh.</th>
                                    <th>Obrat Aktiv</th>
                                    <th>Obrat Zásob</th>
                                    <th>Vzorek</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...reportData.financial_ratios.history].sort((a,b) => b.Year - a.Year).map(r => (
                                    <tr key={r.Year}>
                                        <td><b>{r.Year}</b></td>
                                        <td>{fmtPercent(r["EBITDA marže__median"])}</td>
                                        <td>{fmtPercent(r["Provozní marže__median"])}</td>
                                        <td>{fmtPercent(r["ROA__median"])}</td>
                                        <td>{fmtPercent(r["ROE__median"])}</td>
                                        <td>{fmtPercent(r["ROS__median"])}</td>
                                        <td>{fmt(r["Likvidita běžná__median"])}</td>
                                        <td>{fmt(r["Likvidita pohotová__median"])}</td>
                                        <td>{fmt(r["Likvidita okamžitá__median"])}</td>
                                        <td>{fmtPercent(r["Zadluženost celková__median"])}</td>
                                        <td>{fmtPercent(r["Zadluženost dlouhodobá__median"])}</td>
                                        <td>{fmtInt(r["Obrat aktiv__median"])}</td>
                                        <td>{fmtInt(r["Obrat zásob__median"])}</td>
                                        <td className="muted">{r.n}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                  )}
            </section>
           
            {/* --- TABLE: AGGREGATED SUMMARY --- */}
            <section className="card" style={{ marginTop: '2rem' , padding: '0.5rem', paddingTop: '2rem', paddingBottom: '2rem' }}>
                <div className="card__header" style={{flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', paddingLeft: '1.5rem', paddingRight: '1.5rem'}}>
                        <h4 style={{margin:0}}>Souhrnné ukazatele (Agregace)</h4>
                        <div className="window-switcher">
                            {[
                                { id: "period_all", label: "2016-2025" },
                                { id: "period_2021_2024", label: "2021-2025" },
                                { id: "period_2023_2024", label: "2023-2025" }
                            ].map((p) => (
                                <button
                                    key={p.id}
                                    style={{border: 'none'}} // Added basic inline style for pill
                                    className={summaryPeriod === p.id ? "pill active" : "pill"}
                                    onClick={() => setSummaryPeriod(p.id)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Inserted Description Here */}
                    <div style={{ fontSize: '0.9rem', color: '#5f6b6f', fontStyle: 'italic', paddingLeft: '1.5rem', paddingRight: '1.5rem'}}>
                        {medianDescription}
                    </div>
                </div>
               
                {aggregatedRow ? (
                    <div className="table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Období</th>
                                    <th>EBITDA Marže</th>
                                    <th>Provozní Marže</th>
                                    <th>ROA</th>
                                    <th>ROE</th>
                                    <th>ROS</th>
                                    <th>Likvidita Běžná</th>
                                    <th>Likvidita Pohotová</th>
                                    <th>Likvidita Okamžitá</th>
                                    <th>Zadluženost Celk.</th>
                                    <th>Zadluženost Dlouh.</th>
                                    <th>Obrat Aktiv</th>
                                    <th>Obrat Zásob</th>
                                    <th>Vzorek</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{fontWeight: 'bold', color: '#027360'}}>
                                        {SUMMARY_PERIOD_LABEL[summaryPeriod] || summaryPeriod}
                                    </td>
                                    <td>{fmtPercent(aggregatedRow["EBITDA marže__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["Provozní marže__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["ROA__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["ROE__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["ROS__median"])}</td>
                                    <td>{fmt(aggregatedRow["Likvidita běžná__median"])}</td>
                                    <td>{fmt(aggregatedRow["Likvidita pohotová__median"])}</td>
                                    <td>{fmt(aggregatedRow["Likvidita okamžitá__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["Zadluženost celková__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["Zadluženost dlouhodobá__median"])}</td>
                                    <td>{fmtInt(aggregatedRow["Obrat aktiv__median"])}</td>
                                    <td>{fmtInt(aggregatedRow["Obrat zásob__median"])}</td>
                                    <td>{aggregatedRow.n}</td>
                                </tr>
                            </tbody>
                        </table>
                        <h4 style={{margin: '1.5rem 1.5rem 0.5rem'}}>Pracovní kapitál, přidaná hodnota a růst</h4>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Období</th>
                                    <th title="Krátkodobé pohledávky / tržby × 360">Doba obratu pohledávek</th>
                                    <th title="Krátkodobé závazky / tržby × 360">Doba obratu závazků</th>
                                    <th title="Pohledávky + zásoby − závazky, ve dnech">Cyklus peněz</th>
                                    <th title="(Tržby − výkonová spotřeba) / tržby">Marže přidané hodnoty</th>
                                    <th>Osobní náklady / přidaná hodnota</th>
                                    <th>Podíl osobních nákladů</th>
                                    <th>Meziroční růst tržeb</th>
                                    <th title="Podíl firem se záporným výsledkem před zdaněním">Ztrátové firmy</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{fontWeight: 'bold', color: '#027360'}}>
                                        {SUMMARY_PERIOD_LABEL[summaryPeriod] || summaryPeriod}
                                    </td>
                                    <td>{fmtDays(aggregatedRow["Doba obratu pohledávek__median"])}</td>
                                    <td>{fmtDays(aggregatedRow["Doba obratu závazků__median"])}</td>
                                    <td>{fmtDays(aggregatedRow["Cyklus peněz__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["Marže přidané hodnoty__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["Osobní náklady / přidaná hodnota__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["Podíl osobních nákladů__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["Meziroční růst tržeb__median"])}</td>
                                    <td>{fmtPercent(aggregatedRow["Ztrátová firma__share"])}</td>
                                </tr>
                            </tbody>
                        </table>
                        {aggregatedRow.maly_vzorek === 1 && (
                            <div style={{ padding: '0.5rem 1.5rem', fontSize: '0.9rem', color: '#b45309' }}>
                                Odvětví má méně než 20 firem – hodnoty jsou orientační.
                            </div>
                        )}
                        <div style={{ padding: '1rem', fontSize: '0.9rem', color: '#666' }}>
                            <p>
                                <strong>Tip:</strong> Hodnoty v této tabulce představují mediány (nebo agregované statistiky)
                                vypočítané přes všechny firmy ve zvoleném období.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div style={{padding: '2rem', textAlign: 'center', color: '#999'}}>
                        Data pro toto období nejsou k dispozici.
                    </div>
                )}
            </section>

            {/* --- TABLE: BY COMPANY SIZE --- */}
            {reportData.by_size?.rows?.length > 0 && (
            <section className="card" style={{ marginTop: '2rem', padding: '0.5rem', paddingTop: '2rem', paddingBottom: '2rem' }}>
                <div className="card__header" style={{flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem', paddingLeft: '1.5rem', paddingRight: '1.5rem'}}>
                    <h4 style={{margin: 0}}>Podle velikosti firmy ({reportData.by_size.period})</h4>
                    <div style={{ fontSize: '0.9rem', color: '#5f6b6f', fontStyle: 'italic' }}>
                        Mediány podle počtu zaměstnanců.
                        {reportData.meta.level === "l2" ? " Podrobné odvětví má na členění málo firem, tabulka je za celý nadřazený oddíl." : ""}
                    </div>
                </div>
                <div className="table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Velikost</th>
                                <th>Firem</th>
                                <th>EBITDA Marže</th>
                                <th>ROA</th>
                                <th>Likvidita Běžná</th>
                                <th>Zadluženost Celk.</th>
                                <th>Cyklus peněz</th>
                                <th>Marže přidané hodnoty</th>
                                <th>Meziroční růst tržeb</th>
                                <th>Ztrátové firmy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...reportData.by_size.rows].sort((a, b) => String(a.Velikost).localeCompare(String(b.Velikost))).map((r) => (
                                <tr key={r.Velikost}>
                                    <td style={{fontWeight: 'bold'}}>{String(r.Velikost).slice(2)}{r.maly_vzorek === 1 ? " *" : ""}</td>
                                    <td>{r.n}</td>
                                    <td>{fmtPercent(r["EBITDA marže__median"])}</td>
                                    <td>{fmtPercent(r["ROA__median"])}</td>
                                    <td>{fmt(r["Likvidita běžná__median"])}</td>
                                    <td>{fmtPercent(r["Zadluženost celková__median"])}</td>
                                    <td>{fmtDays(r["Cyklus peněz__median"])}</td>
                                    <td>{fmtPercent(r["Marže přidané hodnoty__median"])}</td>
                                    <td>{fmtPercent(r["Meziroční růst tržeb__median"])}</td>
                                    <td>{fmtPercent(r["Ztrátová firma__share"])}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {reportData.by_size.rows.some((r) => r.maly_vzorek === 1) && (
                        <div style={{ padding: '0.5rem 1.5rem', fontSize: '0.9rem', color: '#666' }}>* méně než 20 firem – orientační hodnoty</div>
                    )}
                </div>
            </section>
            )}

             {/* --- METHODOLOGY INFO --- */}
            <div style={{ marginBottom: '1.5rem' }}>
                <details style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #eee' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#027360' }}>ℹ️ Poznámky k metodice výpočtu ukazatelů</summary>
                   
                    <div style={{ marginTop: '1rem', fontSize: '0.9rem', lineHeight: '1.6', color: '#444' }}>
                        <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#0b1f2e' }}>📘 Transkripce výpočtu ukazatelů</h4>
                        <p style={{ marginBottom: '1rem' }}>
                            Následující přehled shrnuje, jak byly jednotlivé ukazatele a pomocné položky dopočítány ze základních účetních dat.
                            Výpočty vycházejí z dat firem s ročním obratem nad 30 mil. Kč za období 2016–2025.
                        </p>

                        <h5 style={{ fontSize: '1rem', marginTop: '1rem', marginBottom: '0.5rem', borderBottom: '2px solid #eee', paddingBottom: '0.25rem' }}>
                            Základní odvozené veličiny
                        </h5>
                        <ul style={{ paddingLeft: '1.2rem', marginBottom: '1rem' }}>
                            <li>
                                <strong>Tržby celkem</strong> = Tržby za prodej zboží + Tržby za prodej vlastních výrobků a služeb
                            </li>
                            <li>
                                <strong>EBITDA</strong> = EBIT + Odpisy (pokud chybí, dopočteno jako součet EBIT + Odpisy DHM/DNM)
                            </li>
                        </ul>

                        <h5 style={{ fontSize: '1rem', marginTop: '1rem', marginBottom: '0.5rem', borderBottom: '2px solid #eee', paddingBottom: '0.25rem' }}>
                            Finanční ukazatele
                        </h5>
                        <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                            <li style={{ marginBottom: '0.5rem' }}>
                                <strong>ROA (Rentabilita aktiv)</strong> = EBIT / Aktiva celkem<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Efektivita využití aktiv pro tvorbu provozního zisku.</span>
                            </li>
                            <li style={{ marginBottom: '0.5rem' }}>
                                <strong>ROE (Rentabilita vlastního kapitálu)</strong> = Výsledek hospodaření <u>před zdaněním</u> / Vlastní kapitál<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Výnosnost pro vlastníky (počítáno ze zisku před zdaněním).</span>
                            </li>
                            <li style={{ marginBottom: '0.5rem' }}>
                                <strong>ROS (Rentabilita tržeb)</strong> = Výsledek hospodaření <u>před zdaněním</u> / Tržby celkem<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Ziskovost tržeb před zdaněním.</span>
                            </li>
                            <li style={{ marginBottom: '0.5rem' }}>
                                <strong>EBITDA marže</strong> = EBITDA / Tržby celkem<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Provozní výkonnost očištěná o odpisy.</span>
                            </li>
                            <li style={{ marginBottom: '0.5rem' }}>
                                <strong>Provozní marže</strong> = EBIT / Tržby celkem<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Efektivita hlavní činnosti podniku.</span>
                            </li>
                            <li style={{ marginBottom: '0.5rem' }}>
                                <strong>Zadluženost dlouhodobá</strong> = Dlouhodobé závazky / Aktiva celkem<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Podíl dlouhodobého cizího kapitálu na majetku.</span>
                            </li>
                            <li style={{ marginBottom: '0.5rem' }}>
                                <strong>Zadluženost celková</strong> = (Aktiva celkem − Vlastní kapitál) / Aktiva celkem<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Celkový podíl cizích zdrojů financování.</span>
                            </li>
                            <li style={{ marginBottom: '0.5rem' }}>
                                <strong>Likvidita běžná</strong> = Oběžná aktiva / Krátkodobé závazky<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Schopnost krýt krátkodobé závazky oběžnými aktivy.</span>
                            </li>
                            <li style={{ marginBottom: '0.5rem' }}>
                                <strong>Likvidita pohotová</strong> = (Peněžní prostředky + Krátkodobé pohledávky + Dlouhodobé pohledávky) / Krátkodobé závazky<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Schopnost platit bez nutnosti prodeje zásob (konzervativní výpočet).</span>
                            </li>
                            <li>
                                <strong>Likvidita okamžitá</strong> = Peněžní prostředky na účtech / Krátkodobé závazky<br/>
                                <span style={{ color: '#666', fontStyle: 'italic' }}>→ Okamžitá schopnost splácet dluhy hotovostí.</span>
                            </li>
                        </ul>
                    </div>
                </details>
            </div>
          </section>
        </>
      )}
          <section className="seo-info">
              <h2>O reportu, datech a metodice</h2>
              <p>
                Tento report poskytuje detailní přehled finančních ukazatelů pro vybrané odvětví
                dle klasifikace <strong>NACE</strong> za období <strong>2016–2025</strong>.
                Výstupy zahrnují časové řady mediánů, rozptyl hodnot a analýzu růstu (<strong>CAGR</strong>),
                díky čemuž lze rychle porovnat vývoj oboru v čase i jeho stabilitu.
              </p>
              <ul>
                <li><strong>Rentabilita</strong> – marže, ROA/ROE/ROS</li>
                <li><strong>Likvidita</strong> – běžná, pohotová a okamžitá</li>
                <li><strong>Zadluženost</strong> – celková a dlouhodobá</li>
                <li><strong>Obratkovost</strong> – obrat aktiv a zásob</li>
                <li><strong>Růst</strong> – CAGR pro aktiva, tržby, EBITDA a další metriky</li>
              </ul>
              <p>
                Autorem metodiky a zpracování je <strong>Ing. Ivan Bázin</strong>,
                investiční analytik (Genesis Capital), dříve M&amp;A analytik a advisor ve společnosti{" "}
                <a href="https://galikin.cz" target="_blank" rel="noopener noreferrer">Galik&nbsp;In</a>.{" "}
                <Link to="/o-projektu">O projektu a metodice</Link> · <Link to="/obory">Přehled všech oborů</Link>
                V případě dotazů k metodice nebo interpretaci dat napište na{" "}
                <a href="mailto:info@finpilot.cz">info@finpilot.cz</a>.
              </p>
              <p>
                Chcete-li porovnat více odvětví najednou, přejděte na{" "}
                <Link to="/benchmark-obory">benchmark oborů</Link>.
                Pro individuální analýzu konkrétní firmy můžete využít{" "}
                <strong>AI agenta FinPilot</strong>, který umí z účetních výkazů nebo podle IČO
                <strong> odhadnout hodnotu společnosti</strong> a připravit rychlou finanční analýzu.
                Začít můžete na <Link to="/">hlavní stránce</Link>.
              </p>
              <h2>FAQ k oborovým statistikám</h2>
              <ul>
                <li><strong>Jak vybrat správný NACE obor?</strong> Začněte sekcí, následně upřesněte skupinu a podskupinu podle vašeho podnikání.</li>
                <li><strong>Proč jsou použité mediány?</strong> Medián lépe reprezentuje typickou firmu než průměr, protože potlačuje extrémy.</li>
                <li><strong>Kde porovnat obory mezi sebou?</strong> Použijte navazující stránku <Link to="/benchmark-obory">benchmark oborů</Link>.</li>
              </ul>
          </section>

    </div>
  );
};

export default SectionReportPage;